// src/pages/admin/AgentDetails.tsx
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, useMediaQuery, useTheme, Drawer, CircularProgress,
  Alert, Snackbar, Button, TextField, InputAdornment, Card, Chip, Avatar,
  Stack, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Pagination, IconButton, Collapse, Divider, LinearProgress,
  Badge,
} from '@mui/material';
import {
  Refresh, Search, Close, People, CheckCircle, Cancel, WifiOff, Wifi,
  DirectionsBike, LocationOn, KeyboardArrowDown, KeyboardArrowUp, Visibility,
  Assignment, Star, Timer, TrendingUp, LocalShipping, AttachMoney,
  Notifications, FilterList, VerifiedUser, Warning, ErrorOutline,
  OpenInNew, Inventory, Receipt, Phone, Email as EmailIcon,
  Download,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useAgentMonitorListQuery,
  useAgentDetailListQuery,
  AgentMonitorEntry,
  AgentMonitorListRequest,
  AgentDetailData,
} from '../../features/admin/agentDetails.ts';

// ─── CSV Download Hook ────────────────────────────────────────────────────────

function useCSVDownload(endpoint: string) {
  const [loading, setLoading] = useState(false);

  const download = async (params: Record<string, unknown>, filename: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'authkey': '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ...params, download: 1 }),
        }
      );

      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed:', err);
      alert('CSV download failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return { download, loading };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (v: string | number) =>
  `AED ${(Number(v) || 0).toFixed(2)}`;

const formatDate = (d?: string | null) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const formatDateTime = (d?: string | null) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-AE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const getUserInitials = (name: string) => {
  const parts = (name || '').trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (name?.[0] || '?').toUpperCase();
};

const getRatingColor = (rating: string | number | null | undefined) => {
  if (rating === null || rating === undefined || rating === '') return '#6d7175';
  const r = String(rating).toLowerCase();
  if (r.includes('excellent') || r.includes('a+') || r.includes('a')) return '#00783e';
  if (r.includes('good') || r.includes('b'))    return '#2196f3';
  if (r.includes('average') || r.includes('c')) return '#e65100';
  return '#c62828';
};

const getDocStatusColor = (status: string) => {
  if (status === 'approved') return { bgcolor: '#d1f4e0', color: '#00783e' };
  if (status === 'rejected') return { bgcolor: '#ffebee', color: '#c62828' };
  return { bgcolor: '#fff3e0', color: '#e65100' };
};

const pct = (n: number) => `${n.toFixed(1)}%`;

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard: React.FC<{ icon: React.ReactNode; bg: string; value: string | number; label: string }> =
  ({ icon, bg, value, label }) => (
    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex', flexShrink: 0 }}>{icon}</Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.1 }}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </Box>
    </Card>
  );

// ─── Online / Status Chip ─────────────────────────────────────────────────────

const StatusChip: React.FC<{ active: boolean; isOnline: boolean }> = ({ active, isOnline }) => {
  if (!active)   return <Chip size="small" label="Inactive" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '11px' }} />;
  if (!isOnline) return <Chip size="small" label="Offline"  sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '11px' }} />;
  return (
    <Chip
      icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#00783e', ml: 0.5 }} />}
      size="small" label="Online"
      sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '11px' }}
    />
  );
};

// ─── Agent Detail Dialog ──────────────────────────────────────────────────────

const AgentDetailDialog: React.FC<{
  agentId: number | null;
  onClose: () => void;
}> = ({ agentId, onClose }) => {
  const [tab, setTab] = useState(0);

  const { data: detailRes, isLoading, isError, refetch } = useAgentDetailListQuery(
    { agent_id: agentId! },
    { skip: !agentId, refetchOnMountOrArgChange: true }
  );

  const detail: AgentDetailData | null = detailRes?.data || null;

  return (
    <Dialog open={!!agentId} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '14px', maxHeight: '92vh' } }}>
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: '#e3f2fd', borderRadius: '8px', p: 0.8, display: 'flex' }}>
              <DirectionsBike sx={{ color: '#1565c0', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>
                {detail ? detail.full_name : `Agent #${agentId}`}
              </Typography>
              {detail && (
                <Typography variant="caption" color="text.secondary">
                  {detail.email} · {detail.contact}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          mt: 1.5,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '13px', minHeight: 40, color: '#6d7175' },
          '& .Mui-selected': { color: '#202223', fontWeight: 600 },
          '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 2 },
        }}>
          <Tab label="Profile" />
          <Tab label={`Recent Orders (${detail?.recent_orders?.length || 0})`} />
          <Tab label={`Notifications (${detail?.notifications?.length || 0})`} />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={30} sx={{ color: '#1565c0' }} />
          </Box>
        )}
        {isError && (
          <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>
            Failed to load agent details.
          </Alert>
        )}

        {/* ── Tab 0: Profile ── */}
        {!isLoading && !isError && detail && tab === 0 && (
          <Grid container spacing={2}>
            {/* Status banner */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1.5, p: 2, bgcolor: '#fafbfb', borderRadius: '10px', border: '1px solid #e1e3e5', flexWrap: 'wrap', alignItems: 'center' }}>
                <StatusChip active={detail.active} isOnline={detail.is_online} />
                <Chip size="small" icon={<Inventory sx={{ fontSize: '12px !important' }} />}
                  label={`Load: ${detail.current_load}`}
                  sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '11px' }} />
                {detail.google_maps_link && (
                  <Chip size="small" icon={<LocationOn sx={{ fontSize: '12px !important' }} />}
                    label="View on Maps" clickable
                    component="a" href={detail.google_maps_link} target="_blank"
                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '11px' }} />
                )}
              </Box>
            </Grid>

            {/* Info */}
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223', mb: 1.5 }}>Basic Info</Typography>
                <Stack spacing={1}>
                  {[
                    { label: 'Agent ID',    value: `#${detail.agent_id}` },
                    { label: 'Nationality', value: detail.nationality || '—' },
                    { label: 'Address',     value: detail.address     || '—' },
                    { label: 'Joined',      value: formatDateTime(detail.joined_date) },
                    { label: 'Location Updated', value: formatDateTime(detail.location_updated_at) },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0 }}>{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223', wordBreak: 'break-word' }}>{value}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223', mb: 1.5 }}>Contact</Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ bgcolor: '#e3f2fd', borderRadius: '6px', p: 0.6, display: 'flex' }}>
                      <EmailIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 500 }}>{detail.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ bgcolor: '#e8f5e9', borderRadius: '6px', p: 0.6, display: 'flex' }}>
                      <Phone sx={{ fontSize: 16, color: '#2e7d32' }} />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{detail.contact}</Typography>
                  </Box>
                  {detail.last_latitude && detail.last_longitude && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ bgcolor: '#fff3e0', borderRadius: '6px', p: 0.6, display: 'flex' }}>
                        <LocationOn sx={{ fontSize: 16, color: '#e65100' }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6d7175' }}>
                        {detail.last_latitude}, {detail.last_longitude}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* ── Tab 1: Recent Orders ── */}
        {!isLoading && !isError && detail && tab === 1 && (
          <>
            {detail.recent_orders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Receipt sx={{ fontSize: 48, color: '#c9cccf', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No recent orders found</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ borderRadius: '10px', border: '1px solid #e1e3e5', mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafbfb' }}>
                      {['Order', 'Customer', 'Amount', 'Status', 'Assigned', 'Delivered', 'Time(min)'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: '#6d7175', textTransform: 'uppercase', py: 1.2 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.recent_orders.map(order => (
                      <TableRow key={order.order_id} sx={{ '&:hover': { bgcolor: '#fafbfb' }, '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{order.order_id}</Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontFamily: 'monospace' }}>{order.invoice_no}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{order.customer_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{order.customer_contact}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#00783e' }}>{formatCurrency(order.total_order_amount)}</Typography>
                          {order.cash_on_delivery && <Chip size="small" label="COD" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '9px', height: '16px', mt: 0.3 }} />}
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <Chip size="small" label={`#${order.order_status}`}
                            sx={{ bgcolor: order.order_status === 7 ? '#d1f4e0' : order.order_status === 8 ? '#ffebee' : '#e3f2fd',
                              color: order.order_status === 7 ? '#00783e' : order.order_status === 8 ? '#c62828' : '#1565c0',
                              fontWeight: 600, fontSize: '10px', height: '20px' }} />
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="caption" color="text.secondary">{order.assigned_at || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="caption" color="text.secondary">{order.delivered_at || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: order.delivery_minutes && order.delivery_minutes > 60 ? '#c62828' : '#202223' }}>
                            {order.delivery_minutes ?? '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* ── Tab 2: Notifications ── */}
        {!isLoading && !isError && detail && tab === 2 && (
          <>
            {detail.notifications.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Notifications sx={{ fontSize: 48, color: '#c9cccf', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No notifications found</Typography>
              </Box>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {detail.notifications.map(n => (
                  <Box key={n.notif_id} sx={{
                    p: 1.5, borderRadius: '8px', border: '1px solid #e1e3e5',
                    bgcolor: n.read_flag === 0 ? '#f0f7ff' : '#fafbfb',
                    borderLeft: `3px solid ${n.read_flag === 0 ? '#1565c0' : '#e1e3e5'}`,
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: n.read_flag === 0 ? 700 : 500, color: '#202223' }}>{n.title}</Typography>
                        <Typography variant="caption" color="text.secondary">Type: {n.type} {n.order_id ? `· Order #${n.order_id}` : ''}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {n.read_flag === 0 && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1565c0' }} />}
                        <Typography variant="caption" color="text.secondary">{n.sent_at}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained"
          sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const AgentDetails = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [tabValue,      setTabValue]      = useState(0);
  const [searchStr,     setSearchStr]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page,          setPage]          = useState(1);
  const [expandedId,    setExpandedId]    = useState<number | null>(null);
  const [detailAgentId, setDetailAgentId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Filters
  const [activeFilter,  setActiveFilter]  = useState<'' | '0' | '1'>('');
  const [onlineFilter,  setOnlineFilter]  = useState<'' | '0' | '1'>('');

  const rowsPerPage = 10;

  // ── CSV Download ──
  const { download: downloadCSV, loading: csvLoading } = useCSVDownload('/admin/agent-monitor-list');

  const handleDownloadCSV = () => {
    const params: Record<string, unknown> = {};
    if (debouncedSearch)   params.search = debouncedSearch;
    if (activeFilter !== '') params.active = Number(activeFilter);
    if (onlineFilter !== '') params.online = Number(onlineFilter);
    downloadCSV(params, `delivery_agent_monitor_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // Build query params
  const queryParams: AgentMonitorListRequest = useMemo(() => {
    const p: AgentMonitorListRequest = { page, limit: rowsPerPage };
    if (debouncedSearch) p.search = debouncedSearch;
    if (activeFilter !== '') p.active = Number(activeFilter) as 0 | 1;
    if (onlineFilter !== '') p.online = Number(onlineFilter) as 0 | 1;
    return p;
  }, [page, debouncedSearch, activeFilter, onlineFilter]);

  const { data: monitorRes, isLoading, isError, refetch } = useAgentMonitorListQuery(queryParams, {
    pollingInterval: 30000,
    refetchOnFocus: true,
  });

  const agents: AgentMonitorEntry[] = monitorRes?.data || [];
  const summary = monitorRes?.summary;
  const pagination = monitorRes?.pagination;
  const totalPages = pagination?.pages || 0;

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(e.target.value);
    setPage(1);
    clearTimeout((window as any)._agentSearchTimer);
    (window as any)._agentSearchTimer = setTimeout(() => setDebouncedSearch(e.target.value), 400);
  }, []);

  const handleRefresh = async () => {
    try { await refetch(); setSnackbar({ open: true, message: 'Refreshed!', severity: 'success' }); }
    catch { setSnackbar({ open: true, message: 'Error refreshing', severity: 'error' }); }
  };

  const clearFilters = () => {
    setSearchStr(''); setDebouncedSearch('');
    setActiveFilter(''); setOnlineFilter('');
    setPage(1);
  };

  const hasFilters = !!(searchStr || activeFilter !== '' || onlineFilter !== '');

  // Filter tabs: 0=All, 1=Online, 2=Offline, 3=Inactive
  const tabFiltered = useMemo(() => {
    if (tabValue === 0) return agents;
    if (tabValue === 1) return agents.filter(a => a.availability.active && a.availability.is_online);
    if (tabValue === 2) return agents.filter(a => a.availability.active && !a.availability.is_online);
    return agents.filter(a => !a.availability.active);
  }, [agents, tabValue]);

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      {/* Sidebar */}
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={() => setMobileOpen(v => !v)} />
      </Box>
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={() => setMobileOpen(false)} mobileOpen={false} />
      </Drawer>

      {/* Main */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={() => setMobileOpen(v => !v)} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            {/* ── Header ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#202223' }}>Delivery Agent Monitor</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                  Live fleet overview · auto-refreshes every 30s
                </Typography>
              </Box>
              <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                Refresh
              </Button>
            </Box>

            {/* ── Fleet Summary Stats ── */}
            {!isLoading && !isError && summary && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <People       sx={{ color: '#1565c0', fontSize: 22 }} />, bg: '#e3f2fd', value: summary.fleet.total_agents,   label: 'Total Agents'    },
                  { icon: <Wifi         sx={{ color: '#2e7d32', fontSize: 22 }} />, bg: '#e8f5e9', value: summary.fleet.online_now,      label: 'Online Now'      },
                  { icon: <WifiOff      sx={{ color: '#c62828', fontSize: 22 }} />, bg: '#ffebee', value: summary.fleet.offline_now,     label: 'Offline'         },
                  { icon: <DirectionsBike sx={{ color: '#e65100', fontSize: 22 }} />, bg: '#fff3e0', value: summary.fleet.active_agents, label: 'Active Agents'  },
                  { icon: <LocalShipping sx={{ color: '#7b1fa2', fontSize: 22 }} />, bg: '#f3e5f5', value: summary.today.delivered,      label: "Today's Deliveries" },
                  { icon: <TrendingUp   sx={{ color: '#00796b', fontSize: 22 }} />, bg: '#e0f2f1', value: `${summary.today.ontime_pct}%`, label: 'On-Time Rate'  },
                  { icon: <AttachMoney  sx={{ color: '#1565c0', fontSize: 22 }} />, bg: '#e3f2fd', value: formatCurrency(summary.today.today_revenue), label: "Today's Revenue" },
                  { icon: <Warning      sx={{ color: '#c62828', fontSize: 22 }} />, bg: '#ffebee', value: summary.fleet.agents_at_capacity, label: 'At Capacity'  },
                ].map(({ icon, bg, value, label }) => (
                  <Grid item xs={6} sm={3} md={3} lg={1.5} key={label}>
                    <StatCard icon={icon} bg={bg} value={value} label={label} />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* ── Main Card ── */}
            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>

              {/* Tabs */}
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); setPage(1); }}
                  sx={{
                    px: 2,
                    '& .MuiTab-root':       { textTransform: 'none', fontWeight: 500, fontSize: '13px', minHeight: 48, color: '#6d7175' },
                    '& .Mui-selected':      { color: '#202223', fontWeight: 600 },
                    '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 },
                  }}>
                  <Tab label={`All (${summary?.fleet.total_agents || 0})`} />
                  <Tab label={`Online (${summary?.fleet.online_now || 0})`} />
                  <Tab label={`Offline (${summary?.fleet.offline_now || 0})`} />
                  <Tab label={`Inactive (${summary?.fleet.inactive_agents || 0})`} />
                </Tabs>
              </Box>

              {/* Search & Filters */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" placeholder="Search name, email, contact..."
                  value={searchStr} onChange={handleSearch}
                  sx={{ maxWidth: 340, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }}
                />

                {hasFilters && (
                  <Button size="small" variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                    sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px' }}>
                    Clear Filters
                  </Button>
                )}

                {/* Export CSV Button */}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={csvLoading ? <CircularProgress size={14} sx={{ color: '#202223' }} /> : <Download sx={{ fontSize: 16 }} />}
                  onClick={handleDownloadCSV}
                  disabled={csvLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    borderRadius: '8px',
                    borderColor: '#c9cccf',
                    color: '#202223',
                    bgcolor: 'white',
                    px: 2,
                    '&:hover': { borderColor: '#202223', bgcolor: '#f1f2f4' },
                    '&:disabled': { opacity: 0.6 },
                  }}
                >
                  {csvLoading ? 'Downloading…' : 'Export CSV'}
                </Button>

                {pagination && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {pagination.total} agent{pagination.total !== 1 ? 's' : ''} · page {pagination.page}/{pagination.pages}
                  </Typography>
                )}
              </Box>

              {/* Loading */}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                  <CircularProgress size={32} sx={{ color: '#1565c0' }} />
                </Box>
              )}

              {/* Error */}
              {isError && !isLoading && (
                <Box sx={{ p: 3, bgcolor: 'white' }}>
                  <Alert severity="error" action={<Button size="small" onClick={handleRefresh}>Retry</Button>}>
                    Failed to load agent data. Please try again.
                  </Alert>
                </Box>
              )}

              {/* Empty */}
              {!isLoading && !isError && tabFiltered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                  <People sx={{ fontSize: 64, color: '#c9cccf', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {searchStr ? `No results for "${searchStr}"` : 'No agents found'}
                  </Typography>
                </Box>
              )}

              {/* ── Desktop Table ── */}
              {!isLoading && !isError && tabFiltered.length > 0 && (
                <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                  {/* Header */}
                  {!isMobile && (
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '40px 48px minmax(0, 1fr) 160px 100px 100px 110px 120px 110px 100px 80px', minWidth: 1228,
                      gap: 1, px: 2, py: 1.5,
                      borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb',
                    }}>
                      {['', 'ID', 'Agent', 'Status', 'Load', 'Today Del.', 'Success %', 'On-Time %', 'Revenue', 'Rating', 'Actions'].map(h => (
                        <Typography key={h} variant="caption"
                          sx={{ color: '#6d7175', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase' }}>
                          {h}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {tabFiltered.map(agent => {
                    const a   = agent.profile;
                    const av  = agent.availability;
                    const tod = agent.today;
                    const perf= agent.performance;
                    const notif = agent.notifications;
                    const isExpanded = expandedId === a.agent_id;

                    return (
                      <Box key={a.agent_id}>
                        {/* Row */}
                        <Box
                          sx={!isMobile ? {
                            display: 'grid',
                            gridTemplateColumns: '40px 48px minmax(0, 1fr) 160px 100px 100px 110px 120px 110px 100px 80px', minWidth: 1228,
                            gap: 1, px: 2, py: 1.8, alignItems: 'center',
                            borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                            cursor: 'pointer',
                          } : { p: 2, borderBottom: '1px solid #e1e3e5', cursor: 'pointer', '&:hover': { bgcolor: '#f9fafb' } }}
                          onClick={() => setExpandedId(isExpanded ? null : a.agent_id)}
                        >
                          {!isMobile ? (
                            <>
                              {/* Expand */}
                              <IconButton size="small" sx={{ color: '#6d7175', p: 0 }}
                                onClick={e => { e.stopPropagation(); setExpandedId(isExpanded ? null : a.agent_id); }}>
                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>

                              {/* ID */}
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#6d7175' }}>
                                #{a.agent_id}
                              </Typography>

                              {/* Agent */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                                <Badge
                                  overlap="circular"
                                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                  badgeContent={
                                    <Box sx={{
                                      width: 9, height: 9, borderRadius: '50%',
                                      bgcolor: av.is_online ? '#00783e' : av.active ? '#e65100' : '#c9cccf',
                                      border: '1.5px solid white',
                                    }} />
                                  }
                                >
                                  <Avatar sx={{ width: 34, height: 34, bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '13px', fontWeight: 700 }}>
                                    {getUserInitials(a.full_name)}
                                  </Avatar>
                                </Badge>
                                <Box sx={{ overflow: 'hidden' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }} noWrap>
                                    {a.full_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '11px' }}>
                                    {a.email}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Status */}
                              <StatusChip active={av.active} isOnline={av.is_online} />

                              {/* Load */}
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: av.at_capacity ? '#c62828' : '#202223' }}>
                                  {av.current_load}{av.max_capacity > 0 ? `/${av.max_capacity}` : ''}
                                </Typography>
                                {av.max_capacity > 0 && av.load_percent !== null && (
                                  <LinearProgress variant="determinate" value={Math.min(av.load_percent, 100)}
                                    sx={{
                                      height: 3, borderRadius: 2, mt: 0.4, bgcolor: '#e1e3e5',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: av.load_percent >= 100 ? '#c62828' : av.load_percent >= 70 ? '#e65100' : '#00783e',
                                      },
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Today delivered */}
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#202223' }}>
                                {tod.delivered}
                                <Typography component="span" variant="caption" color="text.secondary">/{tod.assigned}</Typography>
                              </Typography>

                              {/* Success % */}
                              <Chip size="small" label={pct(tod.success_rate)}
                                sx={{
                                  bgcolor: tod.success_rate >= 80 ? '#d1f4e0' : tod.success_rate >= 50 ? '#fff3e0' : '#ffebee',
                                  color:   tod.success_rate >= 80 ? '#00783e' : tod.success_rate >= 50 ? '#e65100' : '#c62828',
                                  fontWeight: 700, fontSize: '11px', height: '22px', width: 'fit-content',
                                }}
                              />

                              {/* On-Time % */}
                              <Chip size="small" label={pct(tod.ontime_pct)}
                                sx={{
                                  bgcolor: tod.ontime_pct >= 80 ? '#e3f2fd' : tod.ontime_pct >= 50 ? '#fff3e0' : '#ffebee',
                                  color:   tod.ontime_pct >= 80 ? '#1565c0' : tod.ontime_pct >= 50 ? '#e65100' : '#c62828',
                                  fontWeight: 700, fontSize: '11px', height: '22px', width: 'fit-content',
                                }}
                              />

                              {/* Revenue */}
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2', fontSize: '12px' }}>
                                {formatCurrency(tod.revenue)}
                              </Typography>

                              {/* Rating */}
                              <Typography variant="body2"
                                sx={{ fontWeight: 700, color: getRatingColor(perf.rating), fontSize: '12px' }}>
                                {perf.rating || '—'}
                              </Typography>

                              {/* Actions */}
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="View Detail">
                                  <IconButton size="small"
                                    onClick={e => { e.stopPropagation(); setDetailAgentId(a.agent_id); }}
                                    sx={{ color: '#1565c0', bgcolor: '#e3f2fd', borderRadius: '6px', p: 0.6, '&:hover': { bgcolor: '#bbdefb' } }}>
                                    <Visibility sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                {notif.unread_count > 0 && (
                                  <Tooltip title={`${notif.unread_count} unread`}>
                                    <Badge badgeContent={notif.unread_count} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '9px', minWidth: '14px', height: '14px' } }}>
                                      <IconButton size="small"
                                        sx={{ color: '#e65100', bgcolor: '#fff3e0', borderRadius: '6px', p: 0.6, '&:hover': { bgcolor: '#ffe0b2' } }}>
                                        <Notifications sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Badge>
                                  </Tooltip>
                                )}
                              </Box>
                            </>
                          ) : (
                            /* Mobile card */
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 38, height: 38, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700 }}>
                                    {getUserInitials(a.full_name)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{a.full_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">#{a.agent_id}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <StatusChip active={av.active} isOnline={av.is_online} />
                                  <IconButton size="small" onClick={e => { e.stopPropagation(); setExpandedId(isExpanded ? null : a.agent_id); }}>
                                    {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">Today: {tod.delivered}/{tod.assigned}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#7b1fa2' }}>{formatCurrency(tod.revenue)}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: getRatingColor(perf.rating) }}>{perf.rating || '—'}</Typography>
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        {/* Expanded details */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223' }}>
                                Agent Snapshot
                              </Typography>
                              <Button size="small" startIcon={<Visibility />}
                                onClick={() => setDetailAgentId(a.agent_id)}
                                sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500, color: '#202223', border: '1px solid #c9cccf' }}>
                                Full Detail
                              </Button>
                            </Box>

                            <Grid container spacing={2}>
                              {/* Today */}
                              <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>Today</Typography>
                                  <Stack spacing={0.3} sx={{ mt: 1 }}>
                                    {[
                                      { l: 'Assigned',         v: tod.assigned },
                                      { l: 'Delivered',        v: tod.delivered,        c: '#00783e' },
                                      { l: 'Failed',           v: tod.failed,           c: tod.failed > 0 ? '#c62828' : undefined },
                                      { l: 'Out for Delivery', v: tod.out_for_delivery  },
                                      { l: 'Avg Delivery',     v: `${tod.avg_delivery_min} min` },
                                    ].map(({ l, v, c }) => (
                                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Card>
                              </Grid>

                              {/* Period */}
                              <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>Period</Typography>
                                  <Stack spacing={0.3} sx={{ mt: 1 }}>
                                    {[
                                      { l: 'Assigned',   v: agent.period.assigned },
                                      { l: 'Delivered',  v: agent.period.delivered,  c: '#00783e' },
                                      { l: 'Failed',     v: agent.period.failed,     c: agent.period.failed > 0 ? '#c62828' : undefined },
                                      { l: 'Cancelled',  v: agent.period.cancelled  },
                                      { l: 'Success %',  v: pct(agent.period.success_rate) },
                                    ].map(({ l, v, c }) => (
                                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Card>
                              </Grid>

                              {/* All-time */}
                              <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>All-Time</Typography>
                                  <Stack spacing={0.3} sx={{ mt: 1 }}>
                                    {[
                                      { l: 'Total Assigned',  v: perf.total_assigned },
                                      { l: 'Total Delivered', v: perf.total_delivered, c: '#00783e' },
                                      { l: 'Success %',       v: pct(perf.success_rate)  },
                                      { l: 'On-Time %',       v: pct(perf.ontime_pct)    },
                                      { l: 'Revenue',         v: formatCurrency(perf.total_revenue), c: '#7b1fa2' },
                                    ].map(({ l, v, c }) => (
                                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Card>
                              </Grid>

                              {/* Assignments + Docs */}
                              <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>Assignments (Period)</Typography>
                                  <Stack spacing={0.3} sx={{ mt: 1 }}>
                                    {[
                                      { l: 'Total',      v: agent.assignments.summary.total },
                                      { l: 'Completed',  v: agent.assignments.summary.completed, c: '#00783e' },
                                      { l: 'Dispatches', v: agent.assignments.summary.dispatches },
                                      { l: 'Returns',    v: agent.assignments.summary.returns    },
                                      { l: 'COD Collected', v: `AED ${agent.assignments.summary.cod_total_amount}`, c: '#7b1fa2' },
                                    ].map(({ l, v, c }) => (
                                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Card>
                              </Grid>

                              {/* Current order */}
                              {agent.current_order && (
                                <Grid item xs={12}>
                                  <Card sx={{
                                    p: 2, borderRadius: '8px', bgcolor: 'white',
                                    border: `1px solid ${agent.current_order.sla_breached ? '#ffcdd2' : '#e1e3e5'}`,
                                    borderLeft: `4px solid ${agent.current_order.sla_breached ? '#c62828' : '#1565c0'}`,
                                  }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px' }}>
                                        Current Order
                                      </Typography>
                                      {agent.current_order.sla_breached && (
                                        <Chip size="small" label="SLA Breached"
                                          sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: '10px', height: '18px' }} />
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                      {[
                                        { l: 'Order',    v: `#${agent.current_order.order_id} — ${agent.current_order.invoice_no}` },
                                        { l: 'Customer', v: agent.current_order.customer_name },
                                        { l: 'Address',  v: agent.current_order.delivery_address },
                                        { l: 'Elapsed',  v: `${agent.current_order.elapsed_minutes} min / ${agent.current_order.sla_minutes} SLA`, c: agent.current_order.sla_breached ? '#c62828' : undefined },
                                      ].map(({ l, v, c }) => (
                                        <Box key={l}>
                                          <Typography variant="caption" color="text.secondary">{l}</Typography>
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: c || '#202223' }}>{v}</Typography>
                                        </Box>
                                      ))}
                                      {agent.current_order.destination.google_maps_link && (
                                        <Tooltip title="Open Destination in Maps">
                                          <IconButton size="small" component="a" href={agent.current_order.destination.google_maps_link} target="_blank"
                                            sx={{ color: '#2e7d32', bgcolor: '#e8f5e9', borderRadius: '6px', p: 0.5 }}>
                                            <OpenInNew sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Card>
                                </Grid>
                              )}

                              {/* Documents */}
                              <Grid item xs={12}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', display: 'block', mb: 1 }}>
                                    Documents
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {[
                                      { label: 'Licence',  status: agent.documents.licence.status },
                                      { label: 'Vehicle',  status: agent.documents.vehicle_registration.status },
                                      { label: 'ID Proof', status: agent.documents.id_proof.status },
                                      { label: 'Passport', status: agent.documents.passport.status },
                                    ].map(({ label, status }) => (
                                      <Chip key={label}
                                        icon={
                                          status === 'approved'
                                            ? <VerifiedUser sx={{ fontSize: '12px !important' }} />
                                            : status === 'rejected'
                                              ? <Cancel sx={{ fontSize: '12px !important' }} />
                                              : <Warning sx={{ fontSize: '12px !important' }} />
                                        }
                                        label={`${label}: ${status}`}
                                        size="small"
                                        sx={{ ...getDocStatusColor(status), fontWeight: 600, fontSize: '11px', height: '24px' }}
                                      />
                                    ))}
                                  </Box>
                                </Card>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Pagination */}
              {totalPages > 1 && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'white', borderTop: '1px solid #e1e3e5' }}>
                  <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)}
                    sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }}
                  />
                </Box>
              )}
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Agent Detail Dialog */}
      <AgentDetailDialog
        agentId={detailAgentId}
        onClose={() => setDetailAgentId(null)}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AgentDetails;